import React, { useState, useEffect, useMemo } from 'react';
import Header from './Header';
import Footer from './Footer';

const pageStyle = {
  minHeight: '100vh',
  background: 'white',
  padding: '0',
  margin: '0',
};

const containerStyle = {
  maxWidth: '1200px',
  margin: '0 auto',
  padding: '64px 16px',
};

const pageHeaderStyle = {
  textAlign: 'center',
  marginBottom: '48px',
};

const subtitleStyle = {
  fontSize: 'clamp(1rem, 3vw, 1.25rem)',
  color: '#86868b',
  margin: '0',
};

const cardStyle = {
  background: '#fff',
  borderRadius: '16px',
  boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
  padding: '28px 24px',
  marginBottom: '32px',
};

const osGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
  gap: '24px',
  marginBottom: '48px',
};

const osSectionTitleStyle = {
  fontSize: '1.35rem',
  fontWeight: '600',
  color: '#1d1d1f',
  margin: '0 0 8px 0',
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
};

const osSectionHintStyle = {
  fontSize: '0.875rem',
  color: '#86868b',
  margin: '0 0 16px 0',
};

const emptyPlatformStyle = {
  color: '#86868b',
  fontSize: '0.95rem',
  margin: '0',
  padding: '12px 0',
};

const tableScrollStyle = {
  overflowX: 'auto',
  marginTop: '8px',
};

const loadingStyle = {
  textAlign: 'center',
  padding: '48px 0',
};

const spinnerStyle = {
  width: '48px',
  height: '48px',
  border: '3px solid #f0f0f0',
  borderTop: '3px solid #0071e3',
  borderRadius: '50%',
  animation: 'mypictor-spin 1s linear infinite',
  margin: '0 auto 16px',
};

const errorStyle = {
  textAlign: 'center',
  padding: '48px 0',
};

const errorIconStyle = {
  fontSize: '48px',
  marginBottom: '16px',
};

const retryButtonStyle = {
  background: '#0071e3',
  color: '#fff',
  border: 'none',
  borderRadius: '8px',
  padding: '8px 24px',
  fontSize: '16px',
  cursor: 'pointer',
};

const featuresGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
  gap: '24px',
};

const featureStyle = {
  textAlign: 'center',
};

const featureIconStyle = {
  fontSize: '32px',
  marginBottom: '12px',
};

const featureTitleStyle = {
  fontWeight: '600',
  color: '#1d1d1f',
  marginBottom: '8px',
};

const featureDescStyle = {
  color: '#86868b',
  fontSize: '14px',
};

const requirementsGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
  gap: '24px',
};

const requirementStyle = {
  fontSize: '14px',
  color: '#86868b',
  lineHeight: '1.5',
};

const DOWNLOAD_JSON_URL =
  process.env.REACT_APP_DOWNLOAD_JSON_URL || 'https://downloads.mypictor.com/downloads.json';

/** Toggle when installers + downloads.json flow are ready again. */
const SHOW_DOWNLOAD_BUILDS = false;

const PLATFORM_ORDER = ['macos', 'windows', 'linux'];

function compareVersionDesc(a, b) {
  return b.localeCompare(a, undefined, { numeric: true, sensitivity: 'base' });
}

function isVersionNewerThan(a, b) {
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }) > 0;
}

function sortFilesByVersionDesc(files) {
  return [...files].sort((x, y) => compareVersionDesc(x.version, y.version));
}

function getPlatformIcon(platform) {
  switch (platform) {
    case 'windows':
      return '🪟';
    case 'macos':
      return '🍎';
    case 'linux':
      return '🐧';
    default:
      return '💻';
  }
}

function getPlatformName(platform) {
  switch (platform) {
    case 'windows':
      return 'Windows';
    case 'macos':
      return 'macOS';
    case 'linux':
      return 'Linux';
    default:
      return 'Desktop';
  }
}

function formatReleaseDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '—';
  }
}

function versionLabelText(file) {
  const base = file.version || '—';
  if (file.label) return `${base} (${file.label})`;
  if (file.kind && !file.label) return `${base} (${String(file.kind).toUpperCase()})`;
  return base;
}

const DownloadPage = () => {
  const [downloadsData, setDownloadsData] = useState(null);
  const [loading, setLoading] = useState(SHOW_DOWNLOAD_BUILDS);
  const [error, setError] = useState('');

  const fetchDownloads = async () => {
    if (!SHOW_DOWNLOAD_BUILDS) return;
    try {
      setLoading(true);
      setError('');
      // Avoid stale manifests: R2/custom domains often cache aggressively; do not rely on default fetch cache.
      const response = await fetch(DOWNLOAD_JSON_URL, { cache: 'no-store' });

      if (!response.ok) {
        throw new Error(`Failed to fetch downloads: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      if (!data.files || !Array.isArray(data.files)) {
        throw new Error('Invalid downloads.json format');
      }

      setDownloadsData(data);
    } catch (err) {
      setError(`Unable to fetch download information: ${err.message}. Please try again later.`);
      console.error('Error fetching downloads:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDownloads();
  }, []);

  const filesByPlatform = useMemo(() => {
    if (!downloadsData?.files) {
      return { macos: [], windows: [], linux: [] };
    }
    const map = { macos: [], windows: [], linux: [] };
    downloadsData.files.forEach((f) => {
      const p = (f.platform || '').toLowerCase();
      if (map[p]) map[p].push(f);
    });
    PLATFORM_ORDER.forEach((p) => {
      map[p] = sortFilesByVersionDesc(map[p]);
    });
    return map;
  }, [downloadsData]);

  const globalLatest = useMemo(() => {
    if (!downloadsData?.files?.length) return null;
    return downloadsData.files.reduce((best, f) =>
      isVersionNewerThan(f.version, best.version) ? f : best
    );
  }, [downloadsData]);

  return (
    <div style={pageStyle}>
      <Header />

      <main style={containerStyle}>
        <div style={pageHeaderStyle}>
          <p style={subtitleStyle}>
            {SHOW_DOWNLOAD_BUILDS
              ? 'Choose your platform. All published builds are listed below (newest first).'
              : 'Desktop downloads are temporarily unavailable while we improve the installer experience.'}
          </p>
        </div>

        {!SHOW_DOWNLOAD_BUILDS ? (
          <div style={cardStyle}>
            <p
              style={{
                color: '#86868b',
                textAlign: 'center',
                margin: 0,
                fontSize: '1.05rem',
                lineHeight: 1.5,
              }}
            >
              Windows, macOS, and Linux installers will be listed here again soon. The sections below
              describe what the desktop app will offer.
            </p>
          </div>
        ) : loading ? (
          <div style={cardStyle}>
            <div style={loadingStyle}>
              <div style={spinnerStyle} />
              <p style={{ color: '#86868b' }}>Loading download information...</p>
            </div>
          </div>
        ) : error ? (
          <div style={cardStyle}>
            <div style={errorStyle}>
              <div style={errorIconStyle}>⚠️</div>
              <p style={{ color: '#ff3b30', marginBottom: '16px' }}>{error}</p>
              <button type="button" onClick={fetchDownloads} style={retryButtonStyle}>
                Try Again
              </button>
            </div>
          </div>
        ) : (
          <>
            <div style={osGridStyle}>
              {PLATFORM_ORDER.map((platform) => {
                const list = filesByPlatform[platform] || [];
                return (
                  <div key={platform} style={cardStyle}>
                    <h2 style={osSectionTitleStyle}>
                      <span aria-hidden="true">{getPlatformIcon(platform)}</span>
                      {getPlatformName(platform)}
                    </h2>
                    <p style={osSectionHintStyle}>
                      {list.length === 0
                        ? 'No builds published yet for this platform.'
                        : `${list.length} build${list.length === 1 ? '' : 's'} available`}
                    </p>
                    {list.length === 0 ? (
                      <p style={emptyPlatformStyle}>Check back soon or try another OS above.</p>
                    ) : (
                      <div style={tableScrollStyle}>
                        <table className="download-builds-table">
                          <thead>
                            <tr>
                              <th scope="col">Version</th>
                              <th scope="col">Release date</th>
                            </tr>
                          </thead>
                          <tbody>
                            {list.map((file, idx) => {
                              const rowKey = file.id || `${file.filename}-${file.version}-${idx}`;
                              const versionText = versionLabelText(file);
                              return (
                                <tr key={rowKey}>
                                  <td>
                                    {file.url ? (
                                      <a
                                        href={file.url}
                                        className="download-builds-version-link"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                      >
                                        {versionText}
                                      </a>
                                    ) : (
                                      <span className="download-builds-version-muted">{versionText}</span>
                                    )}
                                  </td>
                                  <td>{formatReleaseDate(file.releaseDate)}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {globalLatest?.releaseNotes ? (
              <div style={cardStyle}>
                <h2
                  style={{
                    fontSize: '1.5rem',
                    fontWeight: '600',
                    color: '#1d1d1f',
                    marginBottom: '16px',
                  }}
                >
                  Release notes (v{globalLatest.version})
                </h2>
                <p style={{ color: '#1d1d1f', whiteSpace: 'pre-line', margin: 0 }}>
                  {globalLatest.releaseNotes}
                </p>
              </div>
            ) : null}
          </>
        )}

        <div style={cardStyle}>
              <h2
                style={{
                  fontSize: '1.5rem',
                  fontWeight: '600',
                  color: '#1d1d1f',
                  marginBottom: '24px',
                }}
              >
                Why Choose MyPictor?
              </h2>
              <div style={featuresGridStyle}>
                <div style={featureStyle}>
                  <div style={featureIconStyle}>🤖</div>
                  <h3 style={featureTitleStyle}>AI-Powered Organization</h3>
                  <p style={featureDescStyle}>Automatically organize your photos using advanced AI</p>
                </div>
                <div style={featureStyle}>
                  <div style={featureIconStyle}>🔍</div>
                  <h3 style={featureTitleStyle}>Smart Search</h3>
                  <p style={featureDescStyle}>Find any photo instantly with natural language search</p>
                </div>
                <div style={featureStyle}>
                  <div style={featureIconStyle}>📱</div>
                  <h3 style={featureTitleStyle}>Cross-Platform</h3>
                  <p style={featureDescStyle}>Works on Windows, macOS, and Linux</p>
                </div>
              </div>
            </div>

            <div style={cardStyle}>
              <h2
                style={{
                  fontSize: '1.5rem',
                  fontWeight: '600',
                  color: '#1d1d1f',
                  marginBottom: '24px',
                }}
              >
                System Requirements
              </h2>
              <div style={requirementsGridStyle}>
                <div>
                  <h3 style={{ fontWeight: '600', color: '#1d1d1f', marginBottom: '12px' }}>
                    🍎 macOS
                  </h3>
                  <ul style={requirementStyle}>
                    <li>• macOS 14 (Sonoma) or later</li>
                    <li>• 16 GB RAM — runs lighter vision models (llava)</li>
                    <li>• 24 GB RAM — recommended for the default AI model (llama3.2-vision)</li>
                    <li>• Docker Desktop installed; allocate at least 12 GB to the Docker VM</li>
                    <li>• 20 GB free disk space (AI model downloads + photo database)</li>
                  </ul>
                </div>
                <div>
                  <h3 style={{ fontWeight: '600', color: '#1d1d1f', marginBottom: '12px' }}>
                    🪟 Windows
                  </h3>
                  <ul style={requirementStyle}>
                    <li>• Windows 11 (64-bit)</li>
                    <li>• 16 GB RAM — runs lighter vision models (llava)</li>
                    <li>• 24 GB RAM — recommended for the default AI model (llama3.2-vision)</li>
                    <li>• Docker Desktop with WSL2 backend</li>
                    <li>• 20 GB free disk space (AI model downloads + photo database)</li>
                    {SHOW_DOWNLOAD_BUILDS ? (
                      <li>
                        • If Microsoft Defender SmartScreen says the app is unrecognized: choose{' '}
                        <strong>More info</strong>, then <strong>Run anyway</strong> (only if you trust
                        this download).
                      </li>
                    ) : null}
                  </ul>
                </div>
                <div>
                  <h3 style={{ fontWeight: '600', color: '#1d1d1f', marginBottom: '12px' }}>
                    🐧 Linux
                  </h3>
                  <ul style={requirementStyle}>
                    <li>• Ubuntu 22.04 LTS / Fedora 40 or later</li>
                    <li>• 16 GB RAM — runs lighter vision models (llava)</li>
                    <li>• 24 GB RAM — recommended for the default AI model (llama3.2-vision)</li>
                    <li>• Docker Engine 20.10+ or Docker Desktop for Linux</li>
                    <li>• 20 GB free disk space (AI model downloads + photo database)</li>
                  </ul>
                </div>
              </div>
            </div>
      </main>

      <Footer />

      <style>{`
        @keyframes mypictor-spin {
          to { transform: rotate(360deg); }
        }
        .download-builds-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.95rem;
        }
        .download-builds-table thead th {
          text-align: left;
          font-weight: 600;
          font-size: 0.75rem;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          color: #86868b;
          padding: 10px 12px 8px 0;
          border-bottom: 1px solid #e8e8ed;
        }
        .download-builds-table thead th:last-child,
        .download-builds-table tbody td:last-child {
          padding-right: 0;
        }
        .download-builds-table tbody td {
          padding: 14px 12px 14px 0;
          border-bottom: 1px solid #f0f0f0;
          color: #1d1d1f;
          vertical-align: top;
        }
        .download-builds-table tbody tr:last-child td {
          border-bottom: none;
        }
        .download-builds-version-link {
          color: #0071e3;
          font-weight: 600;
          text-decoration: none;
        }
        .download-builds-version-link:hover {
          text-decoration: underline;
        }
        .download-builds-version-muted {
          color: #86868b;
          font-weight: 500;
        }
      `}</style>
    </div>
  );
};

export default DownloadPage;
